'use client';
import { Fragment, useRef, FC, useEffect } from 'react';
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import CloseIcon from '@/icons/CloseIcon';
import Svg from '@/components/Svg';
import { storage } from '@/utils/storage';

const noop = () => void 0;

export type ModalPropTypes = {
  onClose: (...args: any) => void;
  show?: boolean;
  isGreetingCard?: boolean;
};

type ClassNameTypes = {
  panel?: {
    root?: string;
  };

  title?: string;

  close?: {
    root?: string;
    icon?: string;
  };
};

interface PropTypes extends ModalPropTypes {
  id: string;
  title?: string;
  children?: React.ReactNode;
  style?: string;
  bodyStyle?: string;
  heightClassName?: string;
  noOverlay?: boolean;
  classNames?: ClassNameTypes;
  preventScroll?: boolean;
  showCloseButton?: boolean;
  closeOnOutsideClick?: boolean;
}

const Modal: FC<PropTypes> = ({
  id,
  show = false,
  onClose,
  title,
  children,
  style = '',
  bodyStyle = '',
  heightClassName = 'max-md:max-h-[95%]',
  noOverlay = false,
  classNames,
  preventScroll = false,
  showCloseButton = true,
  closeOnOutsideClick = true,
}) => {
  const focusRef = useRef(null);

  useEffect(() => {
    if (show && preventScroll) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      if (preventScroll) {
        // Restore scroll position
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [show, preventScroll]);

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-30'
        onClose={closeOnOutsideClick ? onClose : noop}
        initialFocus={focusRef}
        aria-label={id}
        aria-labelledby={id}
        aria-describedby={id}>
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div
            className={twMerge('fixed inset-0', !noOverlay && 'bg-gray-900/70')}
          />
        </TransitionChild>

        <div
          className={twMerge(
            'fixed inset-x-0 flex items-center justify-center rounded-t-md text-center max-md:bottom-0 md:inset-0',
            heightClassName ?? 'max-md:max-h-[95%]'
          )}>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 max-md:translate-y-full'
            enterTo='opacity-100 max-md:-translate-y-0'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 max-md:-translate-y-0'
            leaveTo='opacity-0 max-md:translate-y-full'>
            <DialogPanel
              className={twMerge(
                'relative max-h-[90vh] w-full transform items-center overflow-y-auto rounded-t-md bg-white px-3 py-6 transition-all md:w-3/4 md:rounded-md lg:w-2/4 xl:w-1/3 2xl:max-w-154',
                style
              )}
              onClick={() => {
                if (noOverlay) {
                  onClose();
                }
              }}>
              <div
                aria-label='modal-header'
                className='flex items-center justify-center'>
                <DialogTitle
                  as='h3'
                  className={twMerge(
                    'px-3 py-0 text-xl font-semibold',
                    classNames?.title
                  )}>
                  {title}
                </DialogTitle>
              </div>
              <div
                className={twMerge(
                  'mt-6 space-y-6 px-3 max-md:flex max-md:flex-col',
                  bodyStyle
                )}
                aria-label='modal-body'>
                {children}
              </div>
              {showCloseButton &&
                (id === 'search-manually' ? (
                  storage.get('addressString') !== 'undefined,undefined' &&
                  storage.get('serviceable') === 'true' && (
                    <button
                      type='button'
                      onClick={onClose}
                      className={twMerge(
                        `absolute right-5 top-7 max-md:top-10`,
                        classNames?.close?.root
                      )}>
                      <Svg
                        className={twMerge(
                          `text-current hover:stroke-fig`,
                          'stroke-fig'
                        )}>
                        <CloseIcon />
                      </Svg>
                    </button>
                  )
                ) : (
                  <button
                    type='button'
                    onClick={onClose}
                    className={twMerge(
                      `absolute right-5 top-7 max-md:top-10`,
                      classNames?.close?.root
                    )}>
                    <Svg
                      className={twMerge(
                        `text-current hover:stroke-fig`,
                        id === 'pickup-store'
                          ? 'stroke-fig'
                          : classNames?.close?.icon
                      )}>
                      <CloseIcon />
                    </Svg>
                  </button>
                ))}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
